import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Search, Trash2 } from 'lucide-react'
import { ServiceCategoryIcon } from '@/components/icons/ServiceCategoryIcons'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { FORM_FIELD_WITHIN_CLASS } from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/useDebounce'
import { PICKER_LIMIT } from '@/lib/listFetch'
import { suggestedTechnicianName } from '@/lib/technicianName'
import { useTechnicianSuggestions } from '@/hooks/useJobs'
import type {
  Technician,
  TechnicianSuggestion,
  TechnicianSuggestionQuery,
} from '@/types'

type Factors = TechnicianSuggestion['factors']

const TECHNICIAN_AVATAR_GRADIENTS = [
  'linear-gradient(150deg, #fcf2dc -44.68%, #c87b08 100%)',
  'linear-gradient(150deg, #f2ebfd -44.68%, #7b3bea 100%)',
  'linear-gradient(150deg, #ebf1fe -44.68%, #2e5ce6 100%)',
]

function technicianAvatarGradient(name: string): string {
  const firstCharacter = name.trim().toUpperCase().charCodeAt(0) || 0
  return TECHNICIAN_AVATAR_GRADIENTS[firstCharacter % 3]
}

interface Props {
  selected: Technician[]
  onChange: (next: Technician[]) => void
  layout?: 'default' | 'reassign'
  // Feedback #14 - job context that drives the ranked suggestions. When absent
  // (e.g. no date yet) the dropdown renders an empty state and only the ranking
  // query is skipped.
  context?: TechnicianSuggestionQuery
}

// 2.3.5 Assign technician section. The search input's dropdown surfaces the
// ranked "suggested technicians" shortlist on focus (feedback #14); typing runs
// the WBS "all technicians" full search through the SAME ranking endpoint with a
// name filter, so search hits carry the same factors (availability, travel
// time, route proximity) as the shortlist. Ticking a checkbox assigns the
// technician; the factors captured at that moment are reused to render the same
// Status / travel / proximity columns in the assigned list below.
export function AssignTechnicianSection({
  selected,
  onChange,
  context,
  layout = 'default',
}: Props) {
  const { t: tr } = useTranslation('jobs')
  const isReassign = layout === 'reassign'
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const suggestionsPanelRef = useRef<HTMLDivElement>(null)
  const ignoreNextOutsideClickRef = useRef(false)
  const [suggestionsHeight, setSuggestionsHeight] = useState(0)
  const debounced = useDebounce(term.trim(), 300)
  const searching = debounced.length > 0

  // Factors captured when a technician was picked from the dropdown, keyed by
  // id, so the assigned rows can show the same Status / travel / proximity.
  // Pre-loaded technicians (edit mode) have no captured factors → their columns
  // fall back to "--".
  const [factorsById, setFactorsById] = useState<Record<string, Factors>>({})

  const addTech = (tech: Technician, factors: Factors) => {
    if (selected.some(t => t.id === tech.id)) return
    setFactorsById(prev => ({ ...prev, [tech.id]: factors }))
    onChange([...selected, tech])
  }
  const remove = (id: string) => onChange(selected.filter(t => t.id !== id))

  // Ranked shortlist (shown when the search box is empty). Needs at least a
  // scheduled date to rank meaningfully.
  const canSuggest = !!context?.scheduled_date
  const { data: suggestData, isLoading: suggestLoading } =
    useTechnicianSuggestions(context ?? {}, canSuggest && !searching)

  // WBS "all technicians" full search — same ranking endpoint + a name filter,
  // so hits keep their computed factors. Runs while the dropdown is open and the
  // user has typed a query (works even without a scheduled date).
  const searchQuery: TechnicianSuggestionQuery = {
    ...(context ?? {}),
    search: debounced,
    limit: PICKER_LIMIT,
  }
  const { data: searchData, isFetching: searchFetching } =
    useTechnicianSuggestions(searchQuery, open && searching)

  // Hydrate factors for already-assigned technicians (edit / reassign mode).
  // They arrive from the job detail as plain records with no factors, so their
  // Status / travel / proximity columns would fall back to "--". Fetch the SAME
  // ranking restricted to the ids we don't yet have factors for, then merge the
  // result so the assigned rows show the same computed columns as the dropdown.
  const missingFactorIds = selected
    .filter(t => !factorsById[t.id])
    .map(t => t.id)
  const { data: assignedFactorsData } = useTechnicianSuggestions(
    {
      ...(context ?? {}),
      technician_ids: missingFactorIds,
      limit: missingFactorIds.length,
    },
    missingFactorIds.length > 0
  )
  // Persist query-hydrated factors into state while rendering (React's
  // recommended alternative to setState-in-effect). Once stored, missingFactorIds
  // empties and the hydration query disables.
  const hydrated = assignedFactorsData?.data
  if (hydrated?.length) {
    let needsMerge = false
    for (const { technician } of hydrated) {
      if (!factorsById[technician.id]) {
        needsMerge = true
        break
      }
    }
    if (needsMerge) {
      const next = { ...factorsById }
      for (const { technician, factors } of hydrated) {
        if (!next[technician.id]) next[technician.id] = factors
      }
      setFactorsById(next)
    }
  }

  const selectedIds = new Set(selected.map(t => t.id))
  const rows = searching ? (searchData?.data ?? []) : (suggestData?.data ?? [])
  const loading = searching ? searchFetching : canSuggest && suggestLoading
  const emptyLabel = searching
    ? tr('assign.noSearchResults')
    : tr('assign.noSuggestions')
  const headerLabel = searching
    ? tr('assign.searchResults')
    : tr('assign.suggestedTechnicians')

  // The reassign picker is rendered inside the dialog instead of a portal so
  // its panel can cover the assigned section without covering the footer.
  // Close it after an outside click has reached its original target so a click
  // on Save Assignment is not consumed merely to dismiss the suggestions.
  useEffect(() => {
    if (!isReassign || !open) return

    const handleOutsideClick = (event: MouseEvent) => {
      // Focusing the input opens the panel during pointerdown. The resulting
      // dialog resize can move the input before that same gesture emits click,
      // so its click target may appear outside both picker elements.
      if (ignoreNextOutsideClickRef.current) {
        ignoreNextOutsideClickRef.current = false
        return
      }

      const target = event.target as Node
      if (
        containerRef.current?.contains(target) ||
        suggestionsPanelRef.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }

    document.addEventListener('click', handleOutsideClick)
    return () => document.removeEventListener('click', handleOutsideClick)
  }, [isReassign, open])

  // Reserve exactly the rendered panel height (loading, empty and populated
  // states differ) while the absolutely-positioned panel covers Assigned.
  useLayoutEffect(() => {
    if (!isReassign || !open) return

    const panel = suggestionsPanelRef.current
    if (!panel) return

    const updateHeight = () => {
      const nextHeight = panel.getBoundingClientRect().height
      setSuggestionsHeight(current =>
        current === nextHeight ? current : nextHeight
      )
    }

    updateHeight()
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(updateHeight)
    observer.observe(panel)
    return () => observer.disconnect()
  }, [isReassign, loading, open, rows.length])

  // Estimated travel time → "45 minutes" / "1 hour" / "1 h 30 min"; "--" when
  // the ranking couldn't compute it (e.g. Google Maps unreachable, no coords).
  const formatTravel = (minutes: number | null | undefined): string => {
    if (minutes === null || minutes === undefined) return '--'
    if (minutes < 60) return tr('assign.durationMinutes', { count: minutes })
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m === 0
      ? tr('assign.durationHours', { count: h })
      : tr('assign.durationHoursMinutes', { h, m })
  }
  // Estimated route proximity (straight-line km); "--" when not computed.
  // Figma shows bare "{{km}} km" (not the compact "Proximity …" chip label).
  const formatDistance = (km: number | null | undefined): string =>
    km === null || km === undefined
      ? '--'
      : tr('assign.factors.distance', { km })

  // Shared row body (identity + Status + Skills + travel + proximity) reused by
  // the dropdown and the assigned list; the caller appends a checkbox or a
  // remove button. `factors` is null for pre-loaded technicians (edit mode).
  // Column layout mirrors the job-form Figma card: compact 32px identity,
  // full-height dividers, 12px labels and wrapping skill badges.
  const technicianColumns = (
    technician: Technician,
    factors: Factors | null
  ): ReactNode => {
    const skills = technician.skills ?? []
    const remainingSkills = skills.slice(3)
    const remainingSkillsTooltip = (
      <ul className='list-disc whitespace-nowrap pl-[18px] text-left text-xs font-medium leading-4 text-white'>
        {remainingSkills.map(skill => (
          <li key={skill}>{skill}</li>
        ))}
      </ul>
    )
    const skillsTooltipClass =
      'z-[80] rounded-lg border-0 bg-[#1a1b1e] px-3 py-2 text-white shadow-[0_1px_2px_rgba(38,45,66,0.1),0_1px_2px_rgba(38,45,66,0.05)] [&_svg]:hidden'
    const statusLabel = factors?.available
      ? tr('assign.factors.available')
      : factors?.on_leave
        ? tr('assign.factors.onLeave')
        : tr('assign.factors.unavailable')
    const statusClass = factors?.available
      ? 'bg-[#d1fae5] text-[#065f46]'
      : factors?.on_leave
        ? 'bg-[#ffeab3] text-[#805c00]'
        : 'bg-[#fdd3d0] text-[#900b09]'
    return (
      <>
        {/* Identity */}
        <div className='flex min-w-0 items-center gap-1.5'>
          <Avatar
            name={technician.full_name}
            src={technician.avatar_url}
            className='size-8 shrink-0 bg-transparent font-heading text-sm font-semibold leading-[1.2] text-white'
            style={{
              backgroundImage: technicianAvatarGradient(technician.full_name),
            }}
          />
          <div className='min-w-0 flex-1'>
            <p className='truncate text-sm font-medium leading-[1.4] text-[#171a20]'>
              {suggestedTechnicianName(technician)}
            </p>
          </div>
        </div>

        <span className='hidden h-[100px] w-px -translate-x-1/2 self-start bg-[#e7e9ef] sm:block' />

        {/* Status */}
        <div className='hidden min-w-0 flex-col items-start gap-1.5 sm:flex'>
          <p className='text-xs leading-4 text-[#171a20]'>
            {tr('assign.status')}
          </p>
          {factors ? (
            <span
              className={cn(
                'inline-flex h-6 w-fit max-w-full items-center justify-center overflow-hidden rounded-lg px-2 text-xs font-medium leading-4 text-ellipsis whitespace-nowrap',
                statusClass
              )}
            >
              {statusLabel}
            </span>
          ) : (
            <p className='text-sm leading-[1.4] text-[#3d434e]'>--</p>
          )}
        </div>

        <span className='hidden h-[100px] w-px -translate-x-1/2 self-start bg-[#e7e9ef] sm:block' />

        {/* Skills */}
        <div className='hidden min-w-0 flex-col items-start gap-1.5 sm:flex'>
          <p className='text-xs leading-4 text-[#171a20]'>
            {tr('assign.skills')}
          </p>
          <div className='flex w-full min-w-0 flex-wrap gap-1.5'>
            {skills.slice(0, 3).map(skill => (
              <span
                key={skill}
                className='cursor-default inline-flex h-6 min-w-0 max-w-full items-center justify-center gap-1 overflow-hidden rounded-lg border border-[#e7e9ef] bg-white px-2 text-xs font-medium leading-4 text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)]'
              >
                <ServiceCategoryIcon name={skill} className='size-3.5' />
                <TruncatedTextTooltip
                  content={skill}
                  contentClassName={skillsTooltipClass}
                  className='min-w-0 flex-1 truncate'
                  sideOffset={2}
                  alwaysShow
                >
                  {skill}
                </TruncatedTextTooltip>
              </span>
            ))}
            {remainingSkills.length > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    tabIndex={0}
                    className='cursor-default inline-flex h-6 items-center justify-center rounded-lg border border-[#e7e9ef] bg-white px-2 text-xs font-medium leading-4 text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)]'
                  >
                    +{remainingSkills.length}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side='top'
                  sideOffset={1}
                  className={skillsTooltipClass}
                >
                  {remainingSkillsTooltip}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        <span className='hidden h-[100px] w-px -translate-x-1/2 self-start bg-[#e7e9ef] lg:block' />

        {/* Estimated travel time */}
        <div className='hidden min-w-0 flex-col items-start gap-1.5 lg:flex'>
          <p className='whitespace-nowrap text-xs leading-4 text-[#171a20]'>
            {tr('assign.travelTime')}
          </p>
          <p className='whitespace-nowrap text-xs leading-4 text-[#3d434e]'>
            {formatTravel(factors?.travel_minutes)}
          </p>
        </div>

        <span className='hidden h-[100px] w-px -translate-x-1/2 self-start bg-[#e7e9ef] lg:block' />

        {/* Estimated route proximity */}
        <div className='hidden min-w-0 flex-col items-start gap-1.5 overflow-hidden lg:flex'>
          <p className='truncate text-xs leading-4 text-[#171a20]'>
            {tr('assign.routeProximity')}
          </p>
          <p className='text-xs leading-4 text-[#3d434e]'>
            {formatDistance(factors?.distance_km)}
          </p>
        </div>
      </>
    )
  }

  const searchField = (
    <div
      ref={containerRef}
      className={cn(
        FORM_FIELD_WITHIN_CLASS,
        'transition-colors',
        open && 'bg-[#eef0f4]'
      )}
    >
      <Search className='size-4 shrink-0 text-[#171a20]' />
      <input
        id='technician-search'
        value={term}
        onChange={event => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onPointerDown={() => {
          if (isReassign && !open) {
            ignoreNextOutsideClickRef.current = true
          }
        }}
        onKeyDown={event => {
          if (event.key !== 'Escape' || !open) return
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
        }}
        placeholder={tr('assign.searchTechnicians')}
        autoComplete='off'
        className='min-w-0 flex-1 bg-transparent font-sans text-sm font-normal leading-[1.4] text-[#171a20] outline-none placeholder:text-[#6c7285]'
      />
    </div>
  )

  const suggestionsContent = (
    <>
      <div className='shrink-0 pb-2'>
        <p className='text-sm font-medium leading-[1.4] text-[#3d434e]'>
          {headerLabel}
        </p>
      </div>
      <div className='-mr-2 min-h-0 flex-1 touch-pan-y overflow-x-hidden overflow-y-auto overscroll-contain [scrollbar-gutter:stable] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#cdcdcd] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-2'>
        {loading ? (
          <p className='px-4 py-3 text-sm font-normal leading-[1.4] text-[#6c7285]'>
            {tr('assign.suggestionsLoading')}
          </p>
        ) : rows.length === 0 ? (
          <p className='px-4 py-3 text-sm font-normal leading-[1.4] text-[#6c7285]'>
            {emptyLabel}
          </p>
        ) : (
          <ul className='flex flex-col gap-2'>
            {rows.map(({ technician, factors }) => {
              const picked = selectedIds.has(technician.id)
              const togglePicked = () => {
                if (picked) {
                  remove(technician.id)
                } else {
                  addTech(technician, factors)
                }
              }
              return (
                <li key={technician.id}>
                  <div
                    role='button'
                    tabIndex={0}
                    onClick={togglePicked}
                    onKeyDown={event => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      togglePicked()
                    }}
                    className={cn(
                      'grid min-h-[124px] cursor-pointer grid-cols-[minmax(0,1fr)_24px] items-start gap-2 overflow-hidden rounded-xl p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10b981] focus-visible:ring-offset-2 sm:grid-cols-[minmax(120px,1fr)_0_88px_0_minmax(110px,130px)_24px] lg:grid-cols-[150px_0_88px_0_minmax(110px,1fr)_0_88px_0_72px_24px]',
                      picked ? 'bg-[#f4f5f8]' : 'bg-white hover:bg-[#f4f5f8]'
                    )}
                  >
                    {technicianColumns(technician, factors)}
                    <Checkbox
                      checked={picked}
                      onClick={event => event.stopPropagation()}
                      onCheckedChange={checked =>
                        checked
                          ? addTech(technician, factors)
                          : remove(technician.id)
                      }
                      className='size-6 shrink-0 self-start rounded-lg border-[#b0b5c3] bg-white data-[state=checked]:border-[#10b981] data-[state=checked]:bg-[#10b981] data-[state=checked]:text-white [&_svg]:size-4'
                      aria-label={technician.full_name}
                    />
                  </div>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </>
  )

  const suggestionsClassName =
    'flex max-h-[400px] flex-col overflow-hidden rounded-lg border border-[#e7e9ef] bg-white p-3 shadow-[0_1px_4px_rgba(38,45,66,0.1),0_1px_4px_rgba(38,45,66,0.05)]'

  return (
    <TooltipProvider delayDuration={250}>
      <div
        className={cn(
          isReassign ? (open ? 'space-y-1' : 'space-y-4') : 'space-y-3'
        )}
      >
        <div className='space-y-1'>
          <Label
            htmlFor='technician-search'
            className={cn(
              'text-xs leading-4 text-[#171a20]',
              isReassign ? 'font-normal' : 'font-medium'
            )}
          >
            {tr('assign.technicianLabel')}
          </Label>
          {isReassign ? (
            searchField
          ) : (
            // This picker is also rendered inside modal dialogs. Making the
            // portalled popover modal gives its scroll area its own Radix scroll
            // lock; otherwise the parent Dialog treats wheel/touch events in the
            // portal as background scrolling and prevents them.
            <Popover modal open={open} onOpenChange={setOpen}>
              <PopoverAnchor asChild>{searchField}</PopoverAnchor>
              <PopoverContent
                align='start'
                side='bottom'
                sideOffset={8}
                collisionPadding={8}
                className={cn(
                  'pointer-events-auto z-[60] w-[var(--radix-popover-trigger-width)]',
                  suggestionsClassName
                )}
                onOpenAutoFocus={event => event.preventDefault()}
                onPointerDownOutside={event => {
                  if (containerRef.current?.contains(event.target as Node)) {
                    event.preventDefault()
                  }
                }}
                onInteractOutside={event => {
                  if (containerRef.current?.contains(event.target as Node)) {
                    event.preventDefault()
                  }
                }}
              >
                {suggestionsContent}
              </PopoverContent>
            </Popover>
          )}
        </div>

        <div
          className='relative'
          style={
            isReassign && open
              ? { height: suggestionsHeight || 400 }
              : undefined
          }
        >
          {isReassign && open && (
            <div
              ref={suggestionsPanelRef}
              className={cn(
                'absolute inset-x-0 top-0 z-20 w-full',
                suggestionsClassName
              )}
            >
              {suggestionsContent}
            </div>
          )}

          <div
            className={cn(
              isReassign &&
                open &&
                'pointer-events-none absolute inset-x-0 top-0 invisible'
            )}
          >
            {selected.length === 0 ? (
              <p className='text-sm leading-[1.4] text-[#3d434e]'>
                {tr('assign.noTechniciansAssigned')}
              </p>
            ) : (
              <div className={cn(isReassign ? 'space-y-4' : 'space-y-3')}>
                <p className='text-sm leading-[1.4] text-[#3d434e]'>
                  {tr('assign.assignedTechnicians')}
                </p>
                <ul
                  className={cn(
                    'flex flex-col gap-2',
                    isReassign &&
                      'max-h-[353px] touch-pan-y overflow-y-auto overscroll-contain pr-1'
                  )}
                >
                  {selected.map(t => (
                    <li
                      key={t.id}
                      className={cn(
                        'grid min-h-[124px] grid-cols-[minmax(0,1fr)_24px] items-start gap-2 overflow-hidden rounded-xl bg-[#f4f5f8] p-3 sm:grid-cols-[minmax(120px,1fr)_0_88px_0_minmax(110px,130px)_24px] lg:grid-cols-[150px_0_88px_0_minmax(110px,1fr)_0_88px_0_72px_24px]',
                        isReassign && 'shrink-0'
                      )}
                    >
                      {technicianColumns(t, factorsById[t.id] ?? null)}
                      <Button
                        type='button'
                        variant='ghost'
                        size='icon'
                        className='size-6 shrink-0 self-start rounded-xl p-0 text-[#ef4444] hover:bg-[#fee2e2] hover:text-[#ef4444]'
                        onClick={() => remove(t.id)}
                        aria-label={tr('assign.remove', {
                          name: t.full_name,
                        })}
                      >
                        <Trash2 className='size-5' />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </TooltipProvider>
  )
}
